/**
 * Hook to check if the system is set up (has tenants and auth users)
 */

import { useCallback, useEffect, useState } from 'react';

import log from 'loglevel';

export interface AuthUser {
  email: string;
  name: string;
}

export interface TenantInfo {
  name: string;
  slug: string;
  isActive: boolean;
}

export interface SetupStatus {
  setupComplete: boolean;
  hasTenants: boolean;
  hasUsers: boolean;
  tenantCount?: number;
  users: AuthUser[];
  tenants: TenantInfo[];
  environment?: string;
  isLoading: boolean;
  error: string | null;
}

// `??` so an intentionally-empty VITE_API_ENDPOINT (staging/preview, worker
// proxies API same-origin) stays "" → relative URLs, not localhost.
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';

export function useSetupStatus(): SetupStatus {
  const [status, setStatus] = useState<SetupStatus>({
    setupComplete: true, // Default to true to avoid flash
    hasTenants: true,
    hasUsers: true,
    users: [],
    tenants: [],
    isLoading: true,
    error: null,
  });

  const checkSetupStatus = useCallback(async () => {
    try {
      log.debug('🔍 Checking setup status...');

      const response = await fetch(`${API_ENDPOINT}/health/setup-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Setup status check failed: ${response.status}`);
      }

      const data = await response.json();

      log.info('📊 Setup status:', {
        setupComplete: data.setupComplete,
        hasTenants: data.hasTenants,
        hasUsers: data.hasUsers,
        tenantCount: data.tenantCount,
      });

      setStatus({
        setupComplete: data.setupComplete,
        hasTenants: data.hasTenants,
        hasUsers: data.hasUsers,
        tenantCount: data.tenantCount,
        users: data.users || [],
        tenants: data.tenants || [],
        environment: data.environment,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      log.error('❌ Failed to check setup status:', error);

      // If we can't check, assume setup is complete (don't block the app)
      setStatus({
        setupComplete: true,
        hasTenants: true,
        hasUsers: true,
        users: [],
        tenants: [],
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, []);

  useEffect(() => {
    checkSetupStatus();
  }, [checkSetupStatus]);

  return status;
}

export default useSetupStatus;
